import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import {
  TREQUEST_LINE_SETUP_TICKET_DELETE,
  TREQUEST_LINE_SETUP_TICKET_LISTING,
  TREQUEST_LINE_SETUP_TICKET_SUBTASK_DELETE,
  TREQUEST_LINE_SETUP_TICKET_SUBTASK_LIST,
  TREQUEST_LINE_SETUP_TICKET_SUBTASK_CREATE,
  TREQUEST_LINE_SETUP_TICKET_UPDATE,
  TREQUEST_LINE_SETUP_TICKET_UPDATE_MECHANIC,
  TLineSetupTicketId,
  LINE_SETUP_TICKET_STATUSES,
  TUserPaginationOption,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';
import { TMachineMaintenanceState } from '../reducers';
import * as TQcoActions from './qco.actions';
import { LINE_SETUP_TICKET_DEFAULT_PAGINATION_OPTION } from './qco.models';
import * as TQcoSelectors from './qco.selectors';

@Injectable({ providedIn: 'root' })
export class QcoFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private machineMaintenanceDataService: MachineMaintenanceDataService,
    private activatedRoute: ActivatedRoute,
  ) {}

  resetLineSetupTicketListing() {
    const bucketFromQuery = this.activatedRoute.snapshot.queryParamMap.get('bucket') ?? '';

    this.fetchLineSetupTicketListing(
      {
        bucket: LINE_SETUP_TICKET_STATUSES.find((bucket) => bucket === bucketFromQuery) ?? 'UPCOMING',
        itemsPerPage: LINE_SETUP_TICKET_DEFAULT_PAGINATION_OPTION.pageSize,
        page: LINE_SETUP_TICKET_DEFAULT_PAGINATION_OPTION.pageNo,
        selectedFilters: [],
      },
      true,
    );
  }

  fetchLineSetupTicketListing(payload: TREQUEST_LINE_SETUP_TICKET_LISTING, resetPaginationOption?: boolean) {
    return this.store.dispatch(new TQcoActions.GetLineSetupTicketsListing(payload, resetPaginationOption));
  }

  getLineSetupTicketListing() {
    return this.store.pipe(select(TQcoSelectors.getLineSetupTickets));
  }

  async deleteLineSetupTicket(payload: TREQUEST_LINE_SETUP_TICKET_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.deleteLineSetupTicket(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async lineSetupTicketUpdateMechanics(payload: TREQUEST_LINE_SETUP_TICKET_UPDATE_MECHANIC) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.updateLineSetupTicketMechanic(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async updateLineSetupTicket(payload: TREQUEST_LINE_SETUP_TICKET_UPDATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.updateLineSetupTicket(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchLineSetupTicketNewSetup(lineSetupTicketId: TLineSetupTicketId) {
    return this.store.dispatch(new TQcoActions.FetchLineSetupTicketNewSetup(lineSetupTicketId));
  }

  fetchLineSetupTicketCurrentSetup(locationId: number) {
    return this.store.dispatch(new TQcoActions.FetchLineSetupTicketCurrentSetup(locationId));
  }

  getLineSetupTicketNewSetup() {
    return this.store.pipe(select(TQcoSelectors.getLineSetupNewSetup));
  }

  getLineSetupTicketCurrentSetup() {
    return this.store.pipe(select(TQcoSelectors.getLineSetupCurrentSetup));
  }

  getLineSetupTicketPaginationOption() {
    return this.store.pipe(select(TQcoSelectors.getLineSetupPaginationOption));
  }

  setLineSetupTicketPaginationOption(payload: TUserPaginationOption) {
    return this.store.dispatch(new TQcoActions.SetLineSetupTicketPaginationOption(payload));
  }

  async getAllAssignedMechanicsForLocation(locationId: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.getAllAssignedMechanicsForLocation(locationId).pipe(
        map((res) => res),
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  fetchLineSetupTicketsSubtasks(payload: TREQUEST_LINE_SETUP_TICKET_SUBTASK_LIST) {
    return this.store.dispatch(new TQcoActions.GetLineSetupTicketsSubtasks(payload));
  }

  getLineSetupTicketsSubtasks() {
    return this.store.pipe(select(TQcoSelectors.getLineSetupSubtasks));
  }

  async createLineSetupTicketSubtask(payload: TREQUEST_LINE_SETUP_TICKET_SUBTASK_CREATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.createLineSetupTicketSubTask(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async deleteLineSetupTicketSubtask(payload: TREQUEST_LINE_SETUP_TICKET_SUBTASK_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.machineMaintenanceDataService.deleteLineSetupTicketSubTask(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
}
